/**
 * Frontend-only simulation of a Cloudflare Stream webhook payload.
 * Replace with real WebSocket / SSE from the backend when the Python webhook is live.
 *
 * @see https://developers.cloudflare.com/stream/manage-video-library/using-webhooks/
 */

const timers = new Map();

const DEFAULT_DELAY_MS = 4000;

/**
 * Random delay before the dummy “readyToStream” webhook fires (inspection kit spike).
 * Short uploads may be playable sooner in reality; long encodes take longer — this range mimics that.
 * Always at least 10 seconds per product requirement.
 */
const INSPECTION_MIN_MS = 10000;
const INSPECTION_MAX_MS = 18000;

export function randomInspectionStreamDelayMs() {
  const span = INSPECTION_MAX_MS - INSPECTION_MIN_MS + 1;
  return INSPECTION_MIN_MS + Math.floor(Math.random() * span);
}

function parseDelayMs() {
  const raw = process.env.VUE_APP_SIMULATED_CF_STREAM_DELAY_MS;
  const n = raw != null && raw !== "" ? Number(raw) : Number.NaN;
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_DELAY_MS;
}

/**
 * Public playback URL for a Cloudflare Stream video UID (`iframe.videodelivery.net`).
 * This is the form IQ resolves back to a Stream asset id — a signed
 * `customer-*.cloudflarestream.com` JWT playback URL is opaque to it (ER-12371 AC-9).
 * @param {string} videoUid Cloudflare Stream video UID
 * @returns {string} HTTPS URL, or "" when the uid is unusable.
 */
export function cloudflareStreamIframeSrc(videoUid) {
  const uid = normalizeCloudflareStreamVideoUid(videoUid);
  if (!uid) return "";
  return `https://iframe.videodelivery.net/${uid}`;
}

/**
 * Build the iframe embed HTML used elsewhere in the app (matches MicrolearningViewEdit conventions).
 * @param {string} videoUid Cloudflare Stream video UID
 */
export function cloudflareStreamIframeHtml(videoUid) {
  const src = cloudflareStreamIframeSrc(videoUid);
  if (!src) return "";
  return `<iframe src="${src}" style="width:100%;height:100%;border:none;border-radius:8px;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowfullscreen></iframe>`;
}

/** @see https://developers.cloudflare.com/stream/viewing-videos/displaying-thumbnails/ */
const CF_CUSTOMER_HOST_IN_STRING_RE = /\b(customer-[a-z0-9]+\.cloudflarestream\.com)\b/i;
const CF_VIDEO_DELIVERY_UID_RE = /iframe\.videodelivery\.net\/([a-z0-9_-]+)/i;
const CF_STREAM_UID_IN_CUSTOMER_HOST_RE =
  /customer-[a-z0-9]+\.cloudflarestream\.com\/([a-z0-9_-]+)(?:\/|$)/i;
/** `requireSignedURLs` playback URL: JWT (3 dot-separated base64url segments) in the path. */
const CF_SIGNED_TOKEN_RE =
  /customer-[a-z0-9]+\.cloudflarestream\.com\/([a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+)(?:\/|$)/;

/**
 * True when `value` is a Cloudflare Stream signed playback URL (contains a JWT, not a raw UID).
 * @param {unknown} value
 */
export function isCloudflareSignedUrl(value) {
  if (!value || typeof value !== "string") return false;
  return CF_SIGNED_TOKEN_RE.test(value);
}

/** Decode the `sub` claim (the real Stream video uid) out of a Stream signed JWT. */
function decodeStreamJwtVideoUid(token) {
  try {
    const payloadSegment = token.split(".")[1];
    // JWT segments are base64url (no padding) — convert to standard base64 for atob.
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload?.sub === "string" ? payload.sub : "";
  } catch {
    return "";
  }
}

/** Stringified missing ids (Python `None`, JS `null`/`undefined`) that leak into URLs. */
const CF_PLACEHOLDER_UID_RE = /^(none|null|undefined)$/i;

/**
 * @param {unknown} uid
 * @returns {string} Normalized Stream video UID or "" if unusable.
 */
export function normalizeCloudflareStreamVideoUid(uid) {
  if (uid == null) return "";
  if (typeof uid !== "string" && typeof uid !== "number") return "";
  const s = String(uid).trim();
  if (!s) return "";
  if (!/^[a-z0-9_-]+$/i.test(s)) return "";
  if (CF_PLACEHOLDER_UID_RE.test(s)) return "";
  return s;
}

/**
 * Extract `customer-*.cloudflarestream.com` host from iframe HTML or any URL string.
 * @param {string} [haystack]
 * @returns {string} Lowercase hostname, or "".
 */
export function extractCloudflareStreamCustomerHost(haystack) {
  if (!haystack || typeof haystack !== "string") return "";
  const m = CF_CUSTOMER_HOST_IN_STRING_RE.exec(haystack);
  return m ? m[1].toLowerCase() : "";
}

/**
 * Pull Stream video UID from embed HTML / URLs (matches `cloudflareStreamIframeHtml` and dashboard iframe src).
 * @param {string} [haystack]
 * @returns {string} Normalized UID or "".
 */
export function extractCloudflareStreamVideoUidFromEmbeds(haystack) {
  if (!haystack || typeof haystack !== "string") return "";
  const mVd = CF_VIDEO_DELIVERY_UID_RE.exec(haystack);
  if (mVd) return normalizeCloudflareStreamVideoUid(mVd[1]);
  const mSigned = CF_SIGNED_TOKEN_RE.exec(haystack);
  if (mSigned) {
    const uid = decodeStreamJwtVideoUid(mSigned[1]);
    if (uid) return normalizeCloudflareStreamVideoUid(uid);
  }
  const mCf = CF_STREAM_UID_IN_CUSTOMER_HOST_RE.exec(haystack);
  if (mCf) return normalizeCloudflareStreamVideoUid(mCf[1]);
  return "";
}

/**
 * Still-image thumbnail for a Stream video UID.
 * Prefer `customer-*.cloudflarestream.com` (see dashboard / embed). If `customerHost` is omitted,
 * uses `videodelivery.net` (same UID domain family as `iframe.videodelivery.net`).
 *
 * @param {unknown} videoUid
 * @param {string} [customerHost] e.g. `customer-f33zs165nr7gyfy4.cloudflarestream.com` (no scheme)
 * @returns {string} HTTPS URL or "" if uid is invalid.
 */
export function cloudflareStreamThumbnailJpgUrl(videoUid, customerHost) {
  const uid = normalizeCloudflareStreamVideoUid(videoUid);
  if (!uid) return "";
  const host = String(customerHost ?? "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
  const path = `/${uid}/thumbnails/thumbnail.jpg?time=0s`;
  if (host) {
    return `https://${host}${path}`;
  }
  return `https://videodelivery.net/${encodeURIComponent(uid)}/thumbnails/thumbnail.jpg?time=0s`;
}

export function cancelSimulatedCloudflareStream(key) {
  const t = timers.get(key);
  if (t) {
    clearTimeout(t);
    timers.delete(key);
  }
}

/**
 * After delayMs, invokes onReady with a payload shaped like a Stream notification body.
 *
 * @param {object} opts
 * @param {string} opts.key Unique key (e.g. checkpoint id) to cancel/replace pending timers
 * @param {string} opts.uid Stream video uid (echoed in payload)
 * @param {number} [opts.delayMs]
 * @param {(payload: object) => void} [opts.onReady]
 * @param {(err: unknown) => void} [opts.onError]
 */
export function simulateCloudflareStreamReady({ key, uid, delayMs, onReady, onError }) {
  if (!key) return;
  cancelSimulatedCloudflareStream(key);
  const ms = delayMs == null ? parseDelayMs() : delayMs;
  const t = setTimeout(() => {
    timers.delete(key);
    const payload = {
      uid: uid || null,
      readyToStream: true,
      status: { state: "ready" },
    };
    try {
      onReady?.(payload);
    } catch (e) {
      onError?.(e);
    }
  }, ms);
  timers.set(key, t);
}
